import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuthStore } from '@/stores/auth';
import { LoginRequest } from '@/types';
import { EyeIcon, EyeOffIcon, LogInIcon } from 'lucide-react';
import LoadingSpinner from '@/components/LoadingSpinner';
import GoogleLoginButton from '@/components/GoogleLoginButton';
import { errorLog } from '@/utils/logger';

interface LoginFormData {
  email: string;
  password: string;
}

const LoginPage: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuthStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const loginSecret = import.meta.env.VITE_LOGIN_SECRET || '';
  const showEmailForm = loginSecret !== '' && searchParams.get('secret') === loginSecret;

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<LoginFormData>();

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);

    try {
      const loginData: LoginRequest = {
        email: data.email,
        password: data.password,
      };

      const success = await login(loginData);

      if (success) {
        navigate('/work-sessions');
      } else {
        setError('email', { message: 'Invalid credentials' });
      }
    } catch (error) {
      errorLog('Login error:', error);
      setError('email', { message: 'Login failed' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSuccess = async (credential: string) => {
    setIsLoading(true);

    try {
      const loginData: LoginRequest = {
        email: '', // Will be extracted from Google token
        googleToken: credential,
      };

      const success = await login(loginData);

      if (success) {
        navigate('/work-sessions');
      } else {
        setError('email', { message: 'Google authentication failed' });
      }
    } catch (error) {
      errorLog('Google login error:', error);
      setError('email', { message: 'Google login failed' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleError = (error: any) => {
    errorLog('Google login error:', error);
    setError('email', { message: 'Google authentication failed' });
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-4xl font-bold text-white">Sign in to your account</h2>
        <p className="mt-2 text-lg text-white">
          Track your work hours and earn HEP coins
        </p>
      </div>

      {/* Google Login */}
      <div className="space-y-4">
        <GoogleLoginButton
          onSuccess={handleGoogleSuccess}
          onError={handleGoogleError}
          disabled={isLoading || !import.meta.env.VITE_GOOGLE_CLIENT_ID}
        />

        {showEmailForm && (
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-700" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-[#2a2a2a] text-gray-400">
                Or continue with email
              </span>
            </div>
          </div>
        )}
      </div>

      {showEmailForm && (
        <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-300">
              Email address
            </label>
            <div className="mt-1">
              <input
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address',
                  },
                })}
                type="email"
                className={`heperum-input ${errors.email ? 'input-error' : ''}`}
                placeholder="Enter your email"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-400">{errors.email.message}</p>
              )}
            </div>
          </div>

          {/* Password */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-300">
              Password
            </label>
            <div className="mt-1 relative">
              <input
                {...register('password', {
                  required: 'Password is required',
                  minLength: {
                    value: 6,
                    message: 'Password must be at least 6 characters',
                  },
                })}
                type={showPassword ? 'text' : 'password'}
                className={`heperum-input pr-10 ${errors.password ? 'input-error' : ''}`}
                placeholder="Enter your password"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeOffIcon className="h-5 w-5 text-gray-400" />
                ) : (
                  <EyeIcon className="h-5 w-5 text-gray-400" />
                )}
              </button>
              {errors.password && (
                <p className="mt-1 text-sm text-red-400">{errors.password.message}</p>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full heperum-btn-gold"
            >
              {isLoading ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  Signing in...
                </>
              ) : (
                <>
                  <LogInIcon className="h-4 w-4 mr-2" />
                  Sign in
                </>
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default LoginPage;
